import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, EventEmitter} from '@angular/core';
import {Router} from "@angular/router";
import {SwitchBoard} from "../services/SwitchBoard";
import {StateService} from "../services/StateService";
import {Session} from "../services/Session";
import {CommonModule} from "@angular/common";
import {State} from "../models/State";
import {TOAST_TYPE} from "../constants/Tpiq.constants";
import {LoadingService} from "../services/LoadingService";
import {ToastService} from "../services/Toast.service";
import {TaxPlanningService} from "../tpiq-services/tax-planning.service";
import {takeUntil} from "rxjs/operators";
import {FormsModule} from "@angular/forms";
import {FoundationInit} from "../directives/foundation.directive";
import {BasicPlanLimitService} from '../tpiq-services/basic-plan-limit.service';
import {BasicPlanLimit} from '../models/basic-plan-limit.model';
import {basicLimitReachedMessage, basicTermResetDate} from '../basic-plan-limit/basic-plan-term';
import {MatDialog} from '@angular/material/dialog';
import {
  BasicPlanUpgradeDialogComponent
} from '../basic-plan-limit/basic-plan-upgrade-dialog.component';

declare var jQuery: any;
declare var _: any;
declare const moment: any;

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, FoundationInit],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  isSideMenuExpanded = false;
  searchText: any;
  amount = 0;
  title: string;
  user: any;
  accounts: Array<any> = [];
  showReconnecting: boolean;
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  org: any;
  @ViewChild('tpiq') logoutTitle: ElementRef;
  @ViewChild('apps') appsPanel: ElementRef;
  taxPlanningTerms: any;
  subscriptionStatus: string;
  currentPlan: string;
  onboardStatus: any;
  basicPlanLimit?: BasicPlanLimit;
  basicPlanLimitUnavailable = false;
  readonly entitlementTaxYear = new Date().getFullYear();
  private entitlementLoadSequence = 0;

  constructor(private _router: Router, private switchBoard: SwitchBoard, private stateService: StateService,
              private loadingService: LoadingService,
              private _toastService: ToastService, private taxPlanningService: TaxPlanningService,
              private basicPlanLimits: BasicPlanLimitService, private dialog: MatDialog) {

    this.searchText = '';
    let status: any = Session.get('user').onboardingStatus;
    this.onboardStatus = (status) ? status : 'Done';
    this.user = Session.getUser();
    this.org = Session.getOrg() ? Session.getOrg() : '';
    this.getTermsInfo();

    this.switchBoard.onUserOnboard.pipe(takeUntil(this.destroy$))
      .subscribe(obj => {
        let status: any = Session.get('user').onboardingStatus;
        this.onboardStatus = (status) ? status : 'Done';
      });

    this.switchBoard.onSideBarExpand.pipe(takeUntil(this.destroy$)).subscribe(flag => {
      this.isSideMenuExpanded = flag;
    });

    this.taxPlanningService.getSubscriptionStatus(this.org, this.user.id).subscribe(response => {
      this.subscriptionStatus = response.subscriptionStatus;
      this.currentPlan = response.plan;
      // The subscription-status endpoint refreshes TPS's local HubSpot snapshot.
      // Drop any entitlement response that may have raced that refresh.
      this.basicPlanLimits.invalidate(this.org);
    });
  }

  getTermsInfo() {
    this.taxPlanningService.getTermsAndConditions(Session.getOrg(), this.user.id).subscribe(response => {
      this.taxPlanningTerms = response || {};
    });
  }

  logout() {
    this.validateLoginAndRedirect();
  }

  showError(error) {
  }

  ngOnInit() {
    jQuery(document).ready(function() {
      jQuery(document).foundation();
    });
    this.loadBasicPlanLimit();
    this.basicPlanLimits.invalidated$.pipe(takeUntil(this.destroy$)).subscribe(change => {
      // Credits count per term, so a plan for any tax year changes the counter (CU-868mma4rf).
      if (!change.orgId || change.orgId === this.org) {
        this.loadBasicPlanLimit();
      }
    });
  }

  private loadBasicPlanLimit(forceRefresh = false): void {
    const loadSequence = ++this.entitlementLoadSequence;
    this.basicPlanLimit = undefined;
    this.basicPlanLimitUnavailable = false;
    this.basicPlanLimits.get(this.org, this.user.id, this.entitlementTaxYear, undefined, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: entitlement => {
          if (loadSequence === this.entitlementLoadSequence) {
            this.basicPlanLimit = entitlement;
            this.basicPlanLimitUnavailable = false;
          }
        },
        error: () => {
          if (loadSequence === this.entitlementLoadSequence) {
            this.basicPlanLimit = undefined;
            // Only a Basic firm has a counter to retry.
            this.basicPlanLimitUnavailable = this.basicPlanLimits.isBasicSessionPlan();
          }
        }
      });
  }

  retryBasicPlanLimit(): void {
    this.loadBasicPlanLimit(true);
  }

  showBasicPlanUsage(): void {
    const entitlement = this.basicPlanLimit;
    if (!entitlement?.basicRestrictionsActive) {
      return;
    }
    let message: string;
    if (entitlement.used >= entitlement.limit) {
      message = basicLimitReachedMessage(entitlement);
    } else {
      const resetDate = basicTermResetDate(entitlement);
      const reset = resetDate ? ` Your credits reset on ${resetDate}.` : '';
      message = `Your Basic subscription has used ${entitlement.used} of ${entitlement.limit} plan credits this term.`
        + `${reset} Use the Upgrade link below the plan counter to request an upgrade.`;
    }
    // Sentence case: the shared toast style capitalizes every word.
    this._toastService.pop(TOAST_TYPE.info, message, null, 7000, {sentenceCase: true});
  }

  requestBasicPlanUpgrade(): void {
    this.dialog.open(BasicPlanUpgradeDialogComponent, {
      data: {source: 'PLAN_COUNTER'},
      maxWidth: '95vw',
      width: '520px'
    });
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // CU-868m49jqy: refreshToken()/updateToken() were removed. Both ran only from
  // a switchBoard.onRefresh subscription, and nothing in the app ever emitted
  // onRefresh, so they were unreachable. app.component.ts owns the only token
  // refresh timer.

  validateLoginAndRedirect() {
    const data = localStorage.getItem('wall_rm');
    const details = JSON.parse(data);
    if (details && details.rememberMe) {
      localStorage.removeItem('wall_rm');
      this.switchBoard.onLogOut.next({loggedOut: true});
    } else {
      this.switchBoard.onLogOut.next({loggedOut: true});
    }
  }

  showProfile() {
    // this.switchBoard.onHeaderOptionClicked.next();
    this.stateService.addState(new State('', this._router.url, null, null));
    const link = ['tax-planning/profile'];
    this._router.navigate(link);
  }

  openLogoutMenu($event) {
    $event && $event.preventDefault();
    $event && $event.stopImmediatePropagation();
    // Close apps menu if open
    if (this.appsPanel.nativeElement.classList.contains('is-open')) {
      jQuery('#apps-dropdown-id').foundation('close');
    }
    // Re-read session user/org so dropdown reflects any recent profile/firm edits
    this.user = Session.getUser();
    this.org = Session.getOrg() ? Session.getOrg() : '';
    jQuery('#logout-menu-dropdown-id').foundation('toggle');
  }

  getUserBadgeName(user) {
    if (user) {
      const firstletter = user.first_name ? user.first_name.charAt(0) : '';
      const lastletter = user.last_name ? user.last_name.charAt(0) : '';
      return firstletter + lastletter;
    }
    return '';
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    const base = this;
    if (this.logoutTitle.nativeElement.classList.contains('is-open')) {
      jQuery('#logout-menu-dropdown-id').foundation('toggle');
    }
    if (this.appsPanel.nativeElement.classList.contains('is-open')) {
      this.toggleApps();
    }
  }

  openAppsMenu($event) {
    $event && $event.preventDefault();
    $event && $event.stopImmediatePropagation();
    // Close logout menu if open
    if (this.logoutTitle.nativeElement.classList.contains('is-open')) {
      jQuery('#logout-menu-dropdown-id').foundation('close');
    }
    jQuery('#apps-dropdown-id').foundation('toggle');
  }

  toggleApps() {
    jQuery('#apps-dropdown-id').foundation('toggle');
  }

  manageUsers() {
    this.stateService.addState(new State('', this._router.url, null, null));
    // this.switchBoard.onHeaderOptionClicked.next();
    const link = ['tax-planning/manage-users'];
    this._router.navigate(link);
  }

  manageBilling() {
    this.stateService.addState(new State('', this._router.url, null, null));
    // this.switchBoard.onHeaderOptionClicked.next();
    const link = ['tax-planning/manageBilling'];
    this._router.navigate(link);
  }

  manageSubscription() {
    this.stateService.addState(new State('', this._router.url, null, null));
    const link = ['tax-planning/manage-subscription'];
    this._router.navigate(link);
  }

  manageLogo() {
    this.stateService.addState(new State('', this._router.url, null, null));
    // this.switchBoard.onHeaderOptionClicked.next();
    const link = ['tax-planning/manage-logo'];
    this._router.navigate(link);
  }

  manageQuestionnaire() {
    this.stateService.addState(new State('', this._router.url, null, null));
    // this.switchBoard.onHeaderOptionClicked.next();
    const link = ['tax-planning/manage-questionnaire'];
    this._router.navigate(link);
  }

  manageIntegrations() {
    this.stateService.addState(new State('', this._router.url, null, null));
    // this.switchBoard.onHeaderOptionClicked.next();
    const link = ['tax-planning/integrations'];
    this._router.navigate(link);
  }

  manageAgreements() {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getAgreementsFile(Session.getOrg(), this.user.id).subscribe(response => {
      this.loadingService.triggerLoadingEvent(false);
      if (response) {
          //window.open(response.temporaryURL);
          let org = Session.getOrg();
          const blob = new Blob([response], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = org + '-agreement.pdf';
          a.click();
          window.URL.revokeObjectURL(url);
      }
    }, error => {
      this.loadingService.triggerLoadingEvent(false);
      const message = error.message ? error.message : `Agreement not signed`;
      try {
        this._toastService.pop(TOAST_TYPE.error, message);
      } catch (e) {
        this._toastService.pop(TOAST_TYPE.error, message);
      }
    });
  }

  manageGrowthPlan() {
    this.stateService.addState(new State('', this._router.url, null, null));
    // this.switchBoard.onHeaderOptionClicked.next();
    const link = ['tax-planning/growth-plan'];
    this._router.navigate(link);
  }

  showPage(page) {
    this.stateService.addState(new State('', this._router.url, null, null));
    // this.switchBoard.onHeaderOptionClicked.next();
    this._router.navigate([page]);
  }

}
